import { Tooltip } from '@civilgrid/ui/components';
import capitalize from 'lodash/capitalize';
import type React from 'react';
import { type MutableRefObject, useState } from 'react';
import type { MapObject } from '~/common/types';
import Icon from '~/components2/Icon';
import {
  ArrowIcon,
  ImageIcon,
  LineIcon,
  MarkerIcon,
  PolygonIcon,
  StampIcon,
  TextIcon,
} from '~/pages/projectDetails/civildraw/widgets/Toolbar/ToolbarIcons';
import store from '../../../store';

const OBJECT_TYPE_LABELS: Partial<Record<MapObject['type'], string>> = {
  polygon: 'Polygon',
  line: 'Line',
  marker: 'Marker',
  text: 'Text',
  textV2: 'Text',
  image: 'Image',
  stamp: 'Stamp',
  arrow: 'Arrow',
  circle: 'Circle',
};

interface RowProps {
  object: MapObject;
  isObjectHidden: boolean;
  currentObjectId?: string;
  currentObjectRef: MutableRefObject<MapObject | undefined>;
  onGoToLocation: (object: MapObject) => void;
  tooltipSideOffset?: number;
  defaultExitedObject: { hasExited: boolean; id: string };
  exitedObjectRef: MutableRefObject<{ hasExited: boolean; id: string }>;
  mouseOverObjectId: string | undefined;
  onVisibiltyChange: (id: string, objectType: MapObject['type']) => void;
  id: string;
  isLastObject: boolean;
  setMouseOverObjectId: (value: React.SetStateAction<string | undefined>) => void;
}

export const Row = ({
  object,
  isObjectHidden,
  currentObjectId,
  currentObjectRef,
  onGoToLocation,
  tooltipSideOffset,
  defaultExitedObject,
  exitedObjectRef,
  mouseOverObjectId,
  onVisibiltyChange,
  id,
  isLastObject,
  setMouseOverObjectId,
}: RowProps) => {
  const [isSelected, setIsSelected] = useState(false);
  const objectName = object.title === '' ? 'Untitled Object' : object.title;
  const TypeIcon = ({ type }: { type: string }) => {
    const className = 'h-5 w-5 stroke-grey-60';
    const color = isObjectHidden ? '#D2D4D7' : '#6E7376';
    switch (type) {
      case 'stamp':
        return <StampIcon className={className} color={color} strokeWidth={isSelected ? '1.5' : undefined} />;
      case 'image':
        return <ImageIcon className={className} color={color} strokeWidth={isSelected ? '1.5' : undefined} />;
      case 'line':
        return <LineIcon className={className} color={color} strokeWidth={isSelected ? '8' : undefined} />;
      case 'marker':
        return <MarkerIcon className={className} color={color} strokeWidth={isSelected ? '1.5' : undefined} />;
      case 'textV2':
      case 'text':
        return <TextIcon className={className} color={color} strokeWidth={isSelected ? '1.5' : undefined} />;
      case 'arrow':
        return <ArrowIcon className={className} color={color} strokeWidth={isSelected ? '1.5' : undefined} />;
      default:
        return <PolygonIcon className={className} color={color} strokeWidth={isSelected ? '8' : undefined} />;
    }
  };

  const onEnterLeave = (type: string) => {
    if (type === 'mouseenter') {
      setIsSelected(true);
      setMouseOverObjectId(id);
    } else {
      setIsSelected(false);
      setMouseOverObjectId(undefined);
    }
  };

  return (
    <div onMouseEnter={(e) => onEnterLeave(e.type)} onMouseLeave={(e) => onEnterLeave(e.type)}>
      <div className="flex items-center justify-between px-4 py-2">
        <div className="flex items-center justify-start gap-x-2 truncate">
          <span role="img" aria-label={OBJECT_TYPE_LABELS[object.type] ?? capitalize(object.type)}>
            <TypeIcon type={object.type} />
          </span>
          <div className="flex-1 cursor-pointer truncate">
            <Tooltip asChild text="Click to view object." sideOffset={tooltipSideOffset}>
              <button
                type="button"
                data-testid={`civildraw-object-name-${id}`}
                onClick={() => {
                  if (currentObjectId) {
                    store.resetCurrentObjectId();
                  }
                  if (isObjectHidden) return;
                  exitedObjectRef.current = defaultExitedObject;
                  currentObjectRef.current = object;
                  onGoToLocation(object);
                }}
                className={`flex w-full flex-col text-left focus-ring ${
                  isObjectHidden ? 'cursor-default text-grey-30' : 'cursor-pointer text-grey-60'
                }`}
              >
                <span className={`text-md block truncate ${isSelected ? 'font-bold' : ''}`}>{objectName}</span>

                {/* Hide description for arrows */}
                {object.type !== 'arrow' && <span className="block truncate text-2xs">{object.description}</span>}
              </button>
            </Tooltip>
          </div>
        </div>
        <Tooltip asChild text="Toggle object on/off." sideOffset={tooltipSideOffset}>
          <button
            type="button"
            aria-label={`Show ${objectName}`}
            aria-pressed={!isObjectHidden}
            data-testid={`civildraw-object-visibility-toggle-${id}`}
            className={`flex cursor-pointer appearance-none rounded border-0 bg-transparent p-0 focus-ring ${
              mouseOverObjectId === id
                ? 'pl-2'
                : 'pointer-events-none w-7 opacity-0 focus-visible:pointer-events-auto focus-visible:opacity-100'
            }`}
            onClick={() => onVisibiltyChange(id, object.type)}
          >
            <Icon icon={isObjectHidden ? 'hidden' : 'visible'} />
          </button>
        </Tooltip>
      </div>
      {!isLastObject && <hr className="m-0 w-full border-grey-10 p-0" />}
    </div>
  );
};
